/* MimiBox global UI
   - Product-card Add to Box works through delegated events, including cards injected later.
   - Product cards keep the original Add to Box -> quantity selector behaviour.
   - PDP sticky Add/View Box logic lives in sticky-atc.js.
   - Compact floating View Box appears only after the box has items, never on PDP/cart.
*/
(() => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  let latestCart = null;
  const OPT_KEY = 'mimibox-cart-intent-v2';
  const SNAP_KEY = 'mimibox-cart-snapshot-v2';
  const readStorage = (key) => {
    try {
      return JSON.parse(window.localStorage.getItem(key) || 'null');
    } catch (_) { return null; }
  };
  const writeStorage = (key, value) => {
    try { window.localStorage.setItem(key, JSON.stringify(value)); } catch (_) {}
  };
  const readIntents = () => {
    const value = readStorage(OPT_KEY);
    return Array.isArray(value) ? value : [];
  };
  const recordIntent = (productId, variantId, delta) => {
    if (!productId || !variantId || !delta) return '';
    const intents = readIntents();
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    intents.push({ id, productId: Number(productId), variantId: Number(variantId), delta: Number(delta), at: Date.now() });
    writeStorage(OPT_KEY, intents.slice(-20));
    return id;
  };
  const applyIntents = (cart) => {
    if (!cart) return cart;
    const intents = readIntents();
    if (!intents.length) return cart;
    const next = { ...cart, items: [...(cart.items || [])] };
    intents.forEach((intent) => {
      let item = next.items.find((line) => Number(line.variant_id) === intent.variantId);
      if (!item && intent.delta > 0) {
        item = { product_id: intent.productId, variant_id: intent.variantId, quantity: 0, key: `mimibox-optimistic-${intent.variantId}`, product_title: '', title: '' };
        next.items.push(item);
      }
      if (!item) return;
      item.quantity = Math.max(0, Number(item.quantity || 0) + intent.delta);
      if (item.quantity === 0) next.items = next.items.filter((line) => line !== item);
    });
    next.item_count = next.items.reduce((sum, line) => sum + Number(line.quantity || 0), 0);
    return next;
  };
  const clearIntents = (ids) => {
    if (!ids?.length) return;
    const remove = new Set(ids);
    const remaining = readIntents().filter((intent) => !remove.has(intent.id));
    writeStorage(OPT_KEY, remaining);
  };
  const getCachedCart = () => {
    const snapshot = readStorage(SNAP_KEY);
    if (!snapshot?.cart || Date.now() - Number(snapshot.at || 0) > 10 * 60 * 1000) return null;
    return snapshot.cart;
  };
  const saveCachedCart = (cart) => writeStorage(SNAP_KEY, { at: Date.now(), cart });
  const broadcastCart = (cart) => {
    if (!cart) return;
    latestCart = cart;
    saveCachedCart(cart);
    document.dispatchEvent(new CustomEvent('mimibox:cart-sync', { detail: { cart } }));
  };

  const optimisticCart = () => applyIntents(latestCart || getCachedCart());
  window.MimiBoxCartSync = window.MimiBoxCartSync || {};
  Object.assign(window.MimiBoxCartSync, { recordIntent, applyIntents, clearIntents, readIntents, optimisticCart });

  const getCart = (fresh = false) => {
    const url = fresh ? `${root}cart.js?_mimibox=${Date.now()}` : `${root}cart.js`;
    return fetch(url, {
      cache: fresh ? 'no-store' : 'default',
      headers: { Accept: 'application/json', 'Cache-Control': fresh ? 'no-cache' : 'default' },
      credentials: 'same-origin'
    }).then((response) => {
    if (!response.ok) throw new Error('Cart unavailable');
    return response.json();
    });
  };

  const add = (variantId, quantity = 1) => fetch(`${root}cart/add.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    credentials: 'same-origin',
    body: JSON.stringify({ items: [{ id: Number(variantId), quantity }] })
  }).then(async (response) => {
    const data = await response.json();
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Unable to add item');
    }
    return data;
  });

  const change = (lineId, quantity) => fetch(`${root}cart/change.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    credentials: 'same-origin',
    body: JSON.stringify({ id: lineId, quantity })
  }).then(async (response) => {
    const data = await response.json();
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Unable to update cart');
    }
    return data;
  });

  const refreshCartSections = (open = false) => {
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open } }));
  };

  const remember = (productId, variantId) => {
    if (!productId || !variantId) return;
    try {
      sessionStorage.setItem(`mimibox-variant-${productId}`, String(variantId));
    } catch (_) {}
  };

  const remembered = (productId) => {
    try {
      return sessionStorage.getItem(`mimibox-variant-${productId}`) || '';
    } catch (_) {
      return '';
    }
  };

  const isExcludedPage = () =>
    document.body?.classList.contains('template-product') ||
    document.body?.classList.contains('template-cart');

  /* ---------- Product cards: original Add -> quantity behaviour ---------- */
  const renderCardState = (card, quantity) => {
    const form = card.querySelector('[data-mimibox-card-form]');
    const qtyWrap = card.querySelector('[data-mimibox-card-qty-wrap]');
    const qtyText = card.querySelector('[data-mimibox-quantity]');
    if (!form || !qtyWrap || !qtyText) return;

    qtyText.textContent = String(quantity);
    form.hidden = quantity > 0;
    qtyWrap.hidden = quantity <= 0;
  };

  const syncCards = (cart) => {
    const cards = document.querySelectorAll('[data-mimibox-card-atc]');
    cards.forEach((card) => {
      const productId = Number(card.dataset.productId || 0);
      if (!productId) return;

      const items = (cart.items || []).filter((item) => Number(item.product_id) === productId);
      const savedVariantId = Number(remembered(productId));
      const preferredItem = savedVariantId
        ? items.find((item) => Number(item.variant_id) === savedVariantId)
        : null;
      const item = preferredItem || items[0] || null;

      if (item) {
        card.dataset.variantId = String(item.variant_id);
        remember(productId, item.variant_id);
      }

      const quantity = items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      renderCardState(card, quantity);
    });
  };

  const getCardVariantId = (card, button) => {
    const direct = Number(card?.dataset.variantId || 0);
    if (direct) return direct;
    const formValue = Number(button?.form?.querySelector('[name="id"]')?.value || 0);
    if (formValue) {
      card.dataset.variantId = String(formValue);
      return formValue;
    }
    return 0;
  };

  const readVariantData = (card) => {
    const element = card?.querySelector('[data-mimibox-variant-data]');
    if (!element) return null;
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.error('MimiBox variant data error', error);
      return null;
    }
  };

  const findVariant = (data, selections) =>
    (data?.variants || []).find((variant) =>
      variant.available &&
      variant.options.every((value, index) => String(value) === String(selections[index]))
    ) || null;

  const updateVariantAvailability = (modal, data) => {
    const selects = [...modal.querySelectorAll('[data-mimibox-option-select]')];
    const variants = data?.variants || [];
    const selected = selects.map((select) => select.value);

    selects.forEach((select, index) => {
      [...select.options].forEach((option) => {
        if (!option.value) {
          option.disabled = false;
          return;
        }

        const possible = variants.some((variant) =>
          variant.available &&
          variant.options.every((value, optionIndex) =>
            optionIndex === index || !selected[optionIndex] || String(value) === String(selected[optionIndex])
          ) &&
          String(variant.options[index]) === String(option.value)
        );

        option.disabled = !possible;
      });
    });

    const complete = selected.length === (data?.options || []).length && selected.every(Boolean);
    const variant = complete ? findVariant(data, selected) : null;
    const addButton = modal.querySelector('[data-mimibox-variant-add]');
    const status = modal.querySelector('[data-mimibox-variant-status]');

    if (addButton) addButton.disabled = !variant;
    if (status) {
      status.textContent = variant
        ? (modal.dataset.changeMode === 'true' ? 'Ready to change' : 'Ready to add')
        : 'Select an available option';
    }

    return variant;
  };

  const boxIcon = '<svg class="mimibox-box-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m3.5 7.7 8.5-4 8.5 4v8.6l-8.5 4-8.5-4V7.7Z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/><path d="m3.8 7.9 8.2 4 8.2-4M12 11.9v8.1M8 5.6l8 4" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round" stroke-linecap="round"/></svg>';

  const getVariantModal = () => {
    let modal = document.querySelector('[data-mimibox-variant-modal]');
    if (modal) return modal;

    modal = document.createElement('div');
    modal.className = 'mimibox-variant-modal';
    modal.dataset.mimiboxVariantModal = '';
    modal.setAttribute('aria-hidden', 'true');
    modal.hidden = true;
    modal.innerHTML = `
      <div class="mimibox-variant-modal__backdrop" data-mimibox-variant-close></div>
      <div class="mimibox-variant-modal__panel" role="dialog" aria-modal="true" aria-label="Choose options">
        <button type="button" class="mimibox-variant-modal__close" data-mimibox-variant-close aria-label="Close">×</button>
        <div class="mimibox-variant-modal__icon">${boxIcon}</div>
        <div class="mimibox-variant-modal__eyebrow">ADD TO YOUR BOX</div>
        <h3 data-mimibox-variant-title>Choose your options</h3>
        <div class="mimibox-variant-modal__options" data-mimibox-variant-options></div>
        <div class="mimibox-variant-modal__status" data-mimibox-variant-status>Select an available option</div>
        <button type="button" class="mimibox-variant-modal__add" data-mimibox-variant-add disabled>
          <span data-mimibox-variant-add-label>ADD TO BOX</span>
        </button>
      </div>`;

    document.body.appendChild(modal);
    return modal;
  };

  const closeVariantModal = () => {
    const modal = document.querySelector('[data-mimibox-variant-modal]');
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('mimibox-variant-open');
    window.setTimeout(() => {
      if (!modal.classList.contains('is-open')) modal.hidden = true;
    }, 180);
  };

  const openVariantModal = (card, changeMode = false) => {
    const data = readVariantData(card);
    if (!data?.options || !data?.variants) return;

    const modal = getVariantModal();
    const optionsRoot = modal.querySelector('[data-mimibox-variant-options]');
    const addButton = modal.querySelector('[data-mimibox-variant-add]');
    const title = modal.querySelector('[data-mimibox-variant-title]');
    const label = modal.querySelector('[data-mimibox-variant-add-label]');

    modal.hidden = false;
    modal.dataset.changeMode = changeMode ? 'true' : 'false';
    title.textContent = changeMode ? 'Change your options' : 'Choose your options';
    label.textContent = changeMode ? 'CHANGE IN BOX' : 'ADD TO BOX';
    optionsRoot.innerHTML = '';

    const currentId = Number(card.dataset.variantId || 0);
    const currentVariant = data.variants.find((variant) => Number(variant.id) === currentId);

    data.options.forEach((group, index) => {
      const field = document.createElement('div');
      field.className = 'mimibox-variant-field';

      const labelElement = document.createElement('label');
      labelElement.textContent = group.name;
      labelElement.setAttribute('for', `mimibox-option-${card.dataset.productId}-${index}`);

      const select = document.createElement('select');
      select.id = labelElement.htmlFor;
      select.dataset.mimiboxOptionSelect = '';
      select.setAttribute('aria-label', group.name);
      select.autocomplete = 'off';

      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = `Select ${group.name}`;
      select.appendChild(placeholder);

      (group.values || []).forEach((value) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        select.appendChild(option);
      });

      if (changeMode && currentVariant) {
        select.value = currentVariant.options[index] || '';
      }

      select.addEventListener('change', () => updateVariantAvailability(modal, data));
      field.append(labelElement, select);
      optionsRoot.appendChild(field);
    });

    updateVariantAvailability(modal, data);
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('mimibox-variant-open');

    window.requestAnimationFrame(() => {
      modal.querySelector('select')?.focus();
    });

    addButton.onclick = async () => {
      const variant = updateVariantAvailability(modal, data);
      if (!variant) return;

      addButton.disabled = true;
      const oldVariantId = Number(card.dataset.variantId || 0);

      try {
        if (changeMode && oldVariantId && oldVariantId !== Number(variant.id)) {
          const cart = await getCart();
          const oldItems = (cart.items || []).filter((item) => Number(item.variant_id) === oldVariantId);
          const oldQuantity = oldItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);

          if (oldQuantity > 0) {
            for (const item of oldItems) await change(item.key, 0);
            await add(variant.id, oldQuantity);
          } else {
            await add(variant.id, 1);
          }
        } else if (!changeMode) {
          await add(variant.id, 1);
        }

        card.dataset.variantId = String(variant.id);
        remember(card.dataset.productId, variant.id);
        closeVariantModal();

        const cart = await getCart();
        broadcastCart(cart);
        syncCards(cart);
        updateFloatingViewBox(cart);
        refreshCartSections(false);
      } catch (error) {
        console.error(error);
        addButton.disabled = false;
        const status = modal.querySelector('[data-mimibox-variant-status]');
        if (status) status.textContent = 'Something went wrong. Please try again.';
      }
    };
  };

  const addCardProduct = async (card, button) => {
    if (!card || !button || button.disabled || button.getAttribute('aria-busy') === 'true') return;

    const variantId = getCardVariantId(card, button);
    if (!variantId) return;

    if (card.dataset.hasVariants === 'true') {
      openVariantModal(card, false);
      return;
    }

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      await add(variantId, 1);
      remember(card.dataset.productId, variantId);
      const cart = await getCart();
      broadcastCart(cart);
      syncCards(cart);
      updateFloatingViewBox(cart);
      refreshCartSections(false);
    } catch (error) {
      console.error('MimiBox card add failed', error);
      button.classList.add('is-error');
      const label = button.querySelector('[data-mimibox-card-label]') || button.querySelector('span');
      if (label) {
        const original = label.textContent;
        label.textContent = 'TRY AGAIN';
        window.setTimeout(() => {
          label.textContent = original;
          button.classList.remove('is-error');
        }, 1400);
      }
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };

  const changeCardQuantity = async (card, delta, event) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (!card) return;

    const variantId = Number(card.dataset.variantId || 0);
    if (!variantId) return;

    const controls = card.querySelectorAll('[data-mimibox-minus], [data-mimibox-plus]');
    controls.forEach((control) => { control.disabled = true; });

    try {
      const cart = latestCart || await getCart();
      const item = (cart.items || []).find((line) => Number(line.variant_id) === variantId);
      let updatedCart = null;

      if (delta > 0) {
        if (item) {
          updatedCart = await change(item.key, Number(item.quantity || 0) + 1);
        } else {
          await add(variantId, 1);
          updatedCart = await getCart();
        }
      } else if (item) {
        updatedCart = await change(item.key, Math.max(0, Number(item.quantity || 0) - 1));
      } else {
        updatedCart = cart;
      }

      broadcastCart(updatedCart);
      syncCards(updatedCart);
      updateFloatingViewBox(updatedCart);
      refreshCartSections(false);
    } catch (error) {
      console.error(error);
    } finally {
      controls.forEach((control) => { control.disabled = false; });
    }
  };

  /* ---------- Compact floating View Box ---------- */
  const getFloatingViewBox = () => {
    let button = document.querySelector('[data-mimibox-floating-viewbox]');
    if (button) return button;

    button = document.createElement('button');
    button.type = 'button';
    button.className = 'mimibox-floating-viewbox';
    button.dataset.mimiboxFloatingViewbox = '';
    button.setAttribute('aria-label', 'View Box');
    button.innerHTML = '<span class="mimibox-floating-viewbox__label">VIEW BOX</span><span class="mimibox-floating-viewbox__count" data-mimibox-floating-count>0</span>';

    document.body.appendChild(button);
    button.addEventListener('click', (event) => {
      event.preventDefault();
      const drawer = document.querySelector('m-cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open(button);
        return;
      }
      document.querySelector('.m-cart-icon-bubble')?.click();
    });

    return button;
  };

  const updateFloatingViewBox = (cart) => {
    if (isExcludedPage()) {
      document.querySelector('[data-mimibox-floating-viewbox]')?.remove();
      return;
    }

    const count = Number(cart?.item_count || 0);
    const existing = document.querySelector('[data-mimibox-floating-viewbox]');

    if (count <= 0) {
      existing?.remove();
      return;
    }

    const button = getFloatingViewBox();
    const countNode = button.querySelector('[data-mimibox-floating-count]');
    if (countNode) countNode.textContent = String(count);
  };

  const syncMobileStickyOffset = () => {
    const bar = document.querySelector('.m-mobile-sticky-bar');
    const height = bar && getComputedStyle(bar).display !== 'none' ? bar.getBoundingClientRect().height : 0;
    document.documentElement.style.setProperty('--mimibox-mobile-sticky-height', `${Math.ceil(height)}px`);
  };
  syncMobileStickyOffset();
  window.addEventListener('resize', syncMobileStickyOffset, { passive: true });
  document.addEventListener('shopify:section:load', syncMobileStickyOffset);

  const syncEverything = () => {
    const cached = applyIntents(getCachedCart() || latestCart);
    if (cached) {
      latestCart = cached;
      syncCards(cached);
      updateFloatingViewBox(cached);
    }
    const intentIds = readIntents().map((intent) => intent.id);
    return getCart().then((cart) => {
      if (intentIds.length) clearIntents(intentIds);
      broadcastCart(cart);
      syncCards(cart);
      updateFloatingViewBox(cart);
      return cart;
    }).catch(() => latestCart);
  };

  /* Delegated handlers mean cards created later by recommendations, recently viewed,
     search, sliders or future sections work without another initialization pass. */
  document.addEventListener('pointerdown', (event) => {
    const control = event.target.closest?.('[data-mimibox-card-submit], [data-mimibox-minus], [data-mimibox-plus], [data-mimibox-change]');
    if (!control) return;
    const card = control.closest('[data-mimibox-card-atc]');
    if (!card) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  document.addEventListener('click', (event) => {
    const close = event.target.closest?.('[data-mimibox-variant-close]');
    if (close) {
      event.preventDefault();
      event.stopPropagation();
      closeVariantModal();
      return;
    }

    const plus = event.target.closest?.('[data-mimibox-plus]');
    if (plus) {
      const card = plus.closest('[data-mimibox-card-atc]');
      if (card) {
        changeCardQuantity(card, 1, event);
        return;
      }
    }

    const minus = event.target.closest?.('[data-mimibox-minus]');
    if (minus) {
      const card = minus.closest('[data-mimibox-card-atc]');
      if (card) {
        changeCardQuantity(card, -1, event);
        return;
      }
    }

    const changeButton = event.target.closest?.('[data-mimibox-change]');
    if (changeButton) {
      const card = changeButton.closest('[data-mimibox-card-atc]');
      if (card) {
        event.preventDefault();
        event.stopPropagation();
        openVariantModal(card, true);
        return;
      }
    }

    const submitButton = event.target.closest?.('[data-mimibox-card-submit]');
    if (submitButton) {
      const card = submitButton.closest('[data-mimibox-card-atc]');
      if (card) {
        event.preventDefault();
        event.stopImmediatePropagation();
        addCardProduct(card, submitButton);
      }
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeVariantModal();
  });

  const boot = () => syncEverything();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  const applySyncedCart = (cart) => {
    if (!cart) return;
    broadcastCart(cart);
    syncCards(cart);
    updateFloatingViewBox(cart);
  };

  document.addEventListener('mimibox:cart-sync', (event) => {
    applySyncedCart(event.detail?.cart);
  });

  document.addEventListener('cart:updated', (event) => {
    const cart = event.detail?.cart;
    if (cart) {
      applySyncedCart(cart);
      return;
    }
    syncEverything();
  });

  /* Mobile browsers can restore a previous page from BFCache/history without
     rerunning theme scripts. On return, force one uncached cart read so the PDP ->
     collection Box state always reflects Shopify's current cart. This runs only
     on a return-to-page event, never continuously. */
  let returnSyncFrame = 0;
  let lastReturnSyncAt = 0;
  let pageWasLeft = false;
  let documentWasHidden = false;
  const syncOnReturn = () => {
    const now = Date.now();
    if (now - lastReturnSyncAt < 800) return;
    if (returnSyncFrame) return;
    lastReturnSyncAt = now;
    returnSyncFrame = window.requestAnimationFrame(() => {
      returnSyncFrame = 0;
      const cached = applyIntents(getCachedCart() || latestCart);
      if (cached) {
        latestCart = cached;
        syncCards(cached);
        updateFloatingViewBox(cached);
      }
      const intentIds = readIntents().map((intent) => intent.id);
      getCart(true).then((cart) => {
        if (intentIds.length) clearIntents(intentIds);
        broadcastCart(cart);
        syncCards(cart);
        updateFloatingViewBox(cart);
        refreshCartSections();
      }).catch(() => {});
    });
  };

  window.addEventListener('pagehide', () => { pageWasLeft = true; }, { passive: true });
  window.addEventListener('pageshow', (event) => {
    if (pageWasLeft || event.persisted) {
      pageWasLeft = false;
      syncOnReturn();
    }
  }, { passive: true });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      documentWasHidden = true;
    } else if (documentWasHidden) {
      documentWasHidden = false;
      syncOnReturn();
    }
  }, { passive: true });

  document.addEventListener('shopify:section:load', () => syncEverything());

  /* One low-cost observer covers product cards that are injected by theme/app JS
     (recently viewed, recommendations, carousels, AJAX sections, future blocks).
     It never performs network requests; it only applies the already-known cart. */
  let cardSyncFrame = 0;
  const observer = new MutationObserver((mutations) => {
    let hasCard = false;
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes || []) {
        if (node.nodeType !== 1) continue;
        if (node.matches?.('[data-mimibox-card-atc]') || node.querySelector?.('[data-mimibox-card-atc]')) {
          hasCard = true;
          break;
        }
      }
      if (hasCard) break;
    }
    if (!hasCard || !latestCart || cardSyncFrame) return;
    cardSyncFrame = window.requestAnimationFrame(() => {
      cardSyncFrame = 0;
      syncCards(latestCart);
    });
  });
  if (document.body) observer.observe(document.body, { childList: true, subtree: true });

  if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents?.cartUpdate) {
    MinimogEvents.subscribe(MinimogTheme.pubSubEvents.cartUpdate, (event) => {
      const cart = event?.cart || event?.data?.cart;
      if (!cart) return;
      broadcastCart(cart);
      syncCards(cart);
      updateFloatingViewBox(cart);
    });
  }
})();

/* Lightweight global INR price normalization. Shopify can render the store's
   configured money format in Liquid, while some theme/app surfaces render prices
   later in JS. Normalize only currency-looking text nodes so the change is universal
   without rebuilding price elements or running periodic polling. */
(() => {
  const CURRENCY_RE = /(?:Rs\.?|₹|INR)\s*([\d,]+)(?:\.\d+)?\s*(?:INR)?/gi;
  const CODE_SUFFIX_RE = /([\d,]+)(?:\.\d+)?\s*INR/gi;

  const normalizeTextNode = (node) => {
    if (!node || !node.nodeValue) return;
    const value = node.nodeValue;
    if (!/(?:Rs\.?|₹|INR)/i.test(value)) return;

    const normalized = value
      .replace(CURRENCY_RE, '₹$1')
      .replace(CODE_SUFFIX_RE, '₹$1');

    if (normalized !== value) node.nodeValue = normalized;
  };

  const normalizeNode = (node) => {
    if (!node) return;
    if (node.nodeType === Node.TEXT_NODE) {
      normalizeTextNode(node);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    if (/^(SCRIPT|STYLE|NOSCRIPT|TEMPLATE)$/.test(node.tagName)) return;

    const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT, {
      acceptNode(textNode) {
        const parent = textNode.parentElement;
        if (parent && /^(SCRIPT|STYLE|NOSCRIPT|TEMPLATE)$/.test(parent.tagName)) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach(normalizeTextNode);
  };

  const boot = () => {
    normalizeNode(document.body);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData') {
          normalizeTextNode(mutation.target);
          return;
        }
        mutation.addedNodes.forEach(normalizeNode);
      });
    });

    if (document.body) observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();

/* PDP sticky Add to Box / View Box. Kept separate from product-card delegation. */
(() => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const getCart = (fresh = false) => {
    const url = fresh ? `${root}cart.js?_mimibox=${Date.now()}` : `${root}cart.js`;
    return fetch(url, {
      cache: fresh ? 'no-store' : 'default',
      headers: { Accept: 'application/json', 'Cache-Control': fresh ? 'no-cache' : 'default' },
      credentials: 'same-origin'
    }).then((response) => {
    if (!response.ok) throw new Error('Cart unavailable');
    return response.json();
    });
  };

  const change = (lineId, quantity) => fetch(`${root}cart/change.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    credentials: 'same-origin',
    body: JSON.stringify({ id: lineId, quantity })
  }).then(async (response) => {
    const data = await response.json();
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Unable to update cart');
    }
    return data;
  });

  const refreshCartSections = () => {
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
  };

  const getVariant = (sticky) => {
    const id = Number(sticky.querySelector('[name="id"]')?.value || 0);
    const dataElement = sticky.querySelector('[data-mimibox-sticky-variants]') || sticky.querySelector('[type="application/json"]');
    if (!id || !dataElement) return null;
    try {
      return (JSON.parse(dataElement.textContent) || []).find((variant) => Number(variant.id) === id) || null;
    } catch (_) {
      return null;
    }
  };

  const getVariantItem = (cart, variantId) =>
    (cart?.items || []).find((item) => Number(item.variant_id) === Number(variantId)) || null;

  const rememberVariant = (productId, variantId) => {
    if (!productId || !variantId) return;
    try {
      sessionStorage.setItem(`mimibox-variant-${productId}`, String(variantId));
    } catch (_) {}
  };

  const render = (sticky, cart) => {
    const productId = Number(sticky.dataset.productId || 0);
    const variant = getVariant(sticky);
    const variantId = Number(variant?.id || sticky.querySelector('[name="id"]')?.value || 0);
    const item = getVariantItem(cart, variantId);
    const quantity = Number(item?.quantity || 0);

    const addButton = sticky.querySelector('[data-mimibox-sticky-add]');
    const label = sticky.querySelector('.mimibox-sticky-add-label');
    const quantityWrap = sticky.querySelector('[data-mimibox-sticky-qty]');
    const quantityValue = sticky.querySelector('[data-mimibox-sticky-quantity]');
    const boxCount = sticky.querySelector('[data-mimibox-box-count]');

    if (!addButton || !label || !quantityWrap || !quantityValue || !boxCount) return;

    quantityValue.textContent = String(quantity || 1);
    boxCount.textContent = String(cart?.item_count || 0);
    boxCount.classList.toggle('is-empty', Number(cart?.item_count || 0) === 0);

    sticky.classList.toggle('is-in-box', quantity > 0);
    label.hidden = quantity > 0;
    quantityWrap.hidden = quantity <= 0;
    addButton.dataset.variantId = String(variantId || '');
    addButton.setAttribute('aria-label', quantity > 0 ? `Quantity ${quantity}` : 'Add to Box');

    /* Variant availability is the only reason the CTA should be disabled. */
    addButton.disabled = !variant?.id || !variant.available;

    if (productId && variantId) rememberVariant(productId, variantId);
  };

  const sync = (sticky, fresh = false) => getCart(fresh).then((cart) => {
    render(sticky, cart);
    return cart;
  }).catch((error) => {
    console.error('MimiBox PDP cart sync failed', error);
    return null;
  });

  const openCartDrawer = (trigger) => {
    const drawer = document.querySelector('m-cart-drawer');
    const cartItems = document.querySelector('m-cart-items');

    if (drawer && typeof drawer.open === 'function') {
      /* The drawer can survive in BFCache with an old server-rendered cart after
         spending time on a PDP. Refresh its Shopify section first so checkout
         always opens against the current cart, without relying on local intent state. */
      if (cartItems) {
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true, trigger } }));
      } else {
        drawer.open(trigger);
      }
      return true;
    }
    const cartLink = document.querySelector('.m-cart-icon-bubble');
    if (cartLink) {
      cartLink.click();
      return true;
    }
    return false;
  };

  const init = () => {
    document.querySelectorAll('.mimibox-sticky-atc sticky-atc').forEach((sticky) => {
      if (sticky.dataset.mimiboxPdpBound === 'true') return;
      sticky.dataset.mimiboxPdpBound = 'true';

      const addButton = sticky.querySelector('[data-mimibox-sticky-add]');
      const viewBoxButton = sticky.querySelector('[data-mimibox-view-box]');
      const minusButton = sticky.querySelector('[data-mimibox-sticky-minus]');
      const plusButton = sticky.querySelector('[data-mimibox-sticky-plus]');
      const mainForm = document.querySelector('.m-product-form--main form[action*="/cart/add"]') || document.querySelector('.m-product-form--main form');

      if (!addButton || !mainForm) return;

      addButton.addEventListener('click', async (event) => {
        event.preventDefault();
        event.stopPropagation();

        /* The +/- controls live inside this button for the original sticky markup. */
        if (event.target.closest('[data-mimibox-sticky-minus]') || event.target.closest('[data-mimibox-sticky-plus]')) return;
        if (addButton.disabled) return;

        const variant = getVariant(sticky);
        if (!variant?.id || !variant.available) return;

        if (typeof validateForm === 'function') {
          try {
            const productInfo = document.querySelector('.m-main-product--info');
            const missing = productInfo ? validateForm(productInfo) : [];
            if (missing && missing.length) {
              missing[0]?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
              return;
            }
          } catch (_) {}
        }

        addButton.disabled = true;
        addButton.setAttribute('aria-busy', 'true');

        try {
          const formData = new FormData(mainForm);
          formData.set('id', String(variant.id));

          const response = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
            body: formData
          });

          const data = await response.json();
          if (!response.ok || data.status) {
            throw new Error(data.description || data.message || 'Unable to add item');
          }

          rememberVariant(sticky.dataset.productId, variant.id);
          const intentId = window.MimiBoxCartSync?.recordIntent?.(sticky.dataset.productId, variant.id, 1);
          const optimistic = window.MimiBoxCartSync?.optimisticCart?.();
          if (optimistic) {
            render(sticky, optimistic);
            document.dispatchEvent(new CustomEvent('mimibox:cart-sync', { detail: { cart: optimistic } }));
          }
          if (intentId) window.MimiBoxCartSync?.clearIntents?.([intentId]);
          refreshCartSections();

          /* Reconcile with Shopify in the background. The UI above never waits for
             this request, so leaving the PDP immediately cannot expose an old cart state. */
          getCart().then((cart) => {
            if (intentId) window.MimiBoxCartSync?.clearIntents?.([intentId]);
            render(sticky, cart);
            document.dispatchEvent(new CustomEvent('mimibox:cart-sync', { detail: { cart } }));
          }).catch(() => {});
        } catch (error) {
          console.error('MimiBox PDP add failed', error);
          addButton.disabled = false;
        } finally {
          addButton.removeAttribute('aria-busy');
        }
      });

      const adjustQuantity = async (delta, event) => {
        event?.preventDefault();
        event?.stopPropagation();

        const variantId = Number(addButton.dataset.variantId || getVariant(sticky)?.id || 0);
        if (!variantId) return;

        const controls = [minusButton, plusButton].filter(Boolean);
        controls.forEach((control) => { control.disabled = true; });

        try {
          const cart = await getCart();
          const item = getVariantItem(cart, variantId);
          if (!item) {
            render(sticky, cart);
            return;
          }

          const variant = getVariant(sticky) || {};
          const rule = variant.quantity_rule || {};
          const increment = Math.max(1, Number(rule.increment || 1));
          const next = Math.max(0, Number(item.quantity || 0) + delta * increment);
          const max = rule.max != null ? Number(rule.max) : null;
          const target = max != null ? Math.min(next, max) : next;

          const updatedCart = await change(item.key, target);
          render(sticky, updatedCart);
          document.dispatchEvent(new CustomEvent('mimibox:cart-sync', { detail: { cart: updatedCart } }));
          refreshCartSections();
        } catch (error) {
          console.error('MimiBox PDP quantity update failed', error);
        } finally {
          controls.forEach((control) => { control.disabled = false; });
        }
      };

      minusButton?.addEventListener('click', (event) => adjustQuantity(-1, event));
      plusButton?.addEventListener('click', (event) => adjustQuantity(1, event));

      /* Open the existing drawer immediately. Do not fire cart:refresh here: that
         event performs a network section refresh and was the source of the delay. */
      viewBoxButton?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        openCartDrawer(viewBoxButton);
      });

      sync(sticky);

      if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents?.variantChange) {
        MinimogEvents.subscribe(MinimogTheme.pubSubEvents.variantChange, (event) => {
          const mainProduct = document.querySelector('product-info[id^="MainProduct"]');
          const isMainProduct = !event?.data?.sectionId || !mainProduct || event.data.sectionId === mainProduct.dataset.sectionId;
          if (isMainProduct) window.requestAnimationFrame(() => sync(sticky));
        });
      }

      document.addEventListener('mimibox:cart-sync', (event) => {
        const cart = event.detail?.cart;
        if (cart) render(sticky, cart);
      });
      document.addEventListener('cart:updated', (event) => {
        const cart = event.detail?.cart;
        if (cart) render(sticky, cart);
        else sync(sticky);
      });
      document.addEventListener('shopify:section:load', () => sync(sticky));
    });
  };

  let pdpReturnSyncAt = 0;
  let pdpPageWasLeft = false;
  let pdpDocumentWasHidden = false;
  const syncPdpOnReturn = () => {
    const now = Date.now();
    if (now - pdpReturnSyncAt < 800) return;
    pdpReturnSyncAt = now;
    window.requestAnimationFrame(() => {
      document.querySelectorAll('.mimibox-sticky-atc sticky-atc').forEach((sticky) => {
        if (sticky.dataset.mimiboxPdpBound === 'true') sync(sticky, true);
      });
    });
  };

  window.addEventListener('pagehide', () => { pdpPageWasLeft = true; }, { passive: true });
  window.addEventListener('pageshow', (event) => {
    if (pdpPageWasLeft || event.persisted) {
      pdpPageWasLeft = false;
      syncPdpOnReturn();
    }
  }, { passive: true });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      pdpDocumentWasHidden = true;
    } else if (pdpDocumentWasHidden) {
      pdpDocumentWasHidden = false;
      syncPdpOnReturn();
    }
  }, { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
